import { getStoredEndpoints } from '../utils/endpointStorage';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.authenticated = false;
    this.retryCount = 0;
    this.maxRetryCount = 5;
    this.retryDelay = 1000; // Start with 1 second
    this.retryTimeout = null;
    this.intentionalClose = false;
    this.connectionCount = 0; // Track how many components are using the connection
    this.connectionUrl = null; // Remember the URL we connected to
    this.connectionPromise = null; // Promise for connection in progress
    this.messageQueue = null; // Queue for messages to send when not connected
    // Add cache storage
    this.cache = {
      prices: new Map(), // Store latest prices by symbol
      lastUpdated: new Map(), // Track when each data type was last updated
      instruments: new Map(), // Store instrument data
      ttl: {
        prices: 2000, // 2 seconds TTL for prices
        instruments: 60000 // 1 minute TTL for instruments
      }
    };
  }

  /**
   * Ensure the URL uses the proper WebSocket protocol and includes the API path suffix
   * @param {string} url - The URL to check and potentially convert
   * @returns {string} - A properly formatted WebSocket URL with correct path
   */
  ensureWebSocketProtocol(url) {
    if (!url) return null;
    
    let processedUrl = url;
    
    // Handle protocol conversion first
    // If already using WebSocket protocol, keep as is
    if (processedUrl.startsWith('ws://') || processedUrl.startsWith('wss://')) {
      // No protocol change needed
    }
    // If using HTTP protocol, convert to WebSocket
    else if (processedUrl.startsWith('http://')) {
      processedUrl = processedUrl.replace('http://', 'ws://');
    }
    // If using HTTPS protocol, convert to WebSocket Secure
    else if (processedUrl.startsWith('https://')) {
      processedUrl = processedUrl.replace('https://', 'wss://');
    }
    // If no protocol specified, assume WebSocket Secure
    else if (!processedUrl.includes('://')) {
      processedUrl = `wss://${processedUrl}`;
    }
    else {
      console.warn('Unusual URL protocol for WebSocket connection:', processedUrl);
    }
    
    // Now ensure the API path suffix is present
    if (!processedUrl.endsWith('/api/v1') && !processedUrl.includes('/api/v1/')) {
      // Remove trailing slash if present before adding suffix
      if (processedUrl.endsWith('/')) {
        processedUrl = processedUrl.slice(0, -1);
      }
      processedUrl += '/api/v1';
      console.log(`Added API path suffix to WebSocket URL: ${processedUrl}`);
    }
    
    return processedUrl;
  }

  /**
   * Get the WebSocket URL from stored endpoints or fall back to default
   * @returns {string} The WebSocket URL to connect to
   */
  getWebSocketUrl() {
    // Try to get the URL from stored endpoints
    const endpoints = getStoredEndpoints();
    
    if (endpoints && endpoints.websocket) {
      const wsUrl = this.ensureWebSocketProtocol(endpoints.websocket);
      console.log('Using WebSocket URL from stored endpoints:', wsUrl);
      return wsUrl;
    }
    
    // Fallback to default URL
    const defaultUrl = 'wss://beta-service.wdtp.io/api/v2';
    console.log('No endpoint URL found in storage, using default:', defaultUrl);
    return defaultUrl;
  }

  /**
   * Connect to WebSocket server
   * This method implements reference counting to ensure only one connection
   * is created even when called from multiple components
   */
  connect(url = null) {
    this.connectionCount++;
    console.log(`WebSocket connection requested. Active reference count: ${this.connectionCount}`);
    
    // If no URL is provided, get it from stored endpoints
    const connectionUrl = url || this.getWebSocketUrl();
    
    // If we already have a connection or are in the process of connecting, just return
    if (this.connectionPromise) {
      console.log('WebSocket connection already in progress, reusing existing connection attempt');
      return this.connectionPromise;
    }
    
    // If already connected, don't try to connect again
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket is already connected, reusing existing connection');
      return Promise.resolve();
    }
    
    // Clear any existing retry timeouts
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    
    // Reset intentional close flag when starting a new connection
    this.intentionalClose = false;
    this.connectionUrl = connectionUrl;
    
    console.log(`Attempting to connect to WebSocket URL: ${connectionUrl} (Attempt ${this.retryCount + 1})`);

    // Create a promise for the connection attempt
    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        this.socket = new WebSocket(connectionUrl);
        this.authenticated = false;
    
        this.socket.onopen = () => {
          console.log('WebSocket connected successfully');
          // Reset retry counter on successful connection
          this.retryCount = 0;
          this.retryDelay = 1000;
          this.emit('connect', {});
          
          // Process any queued messages
          this.processQueue();
          
          // Send a test notification when connected
          setTimeout(() => {
            this.emit('new-notification', {
              type: 'new-notification',
              data: {
                message: 'connected successfully',
                type: 'success',
                status: 'Connected',
                notificationId: `ws-connect-${Date.now()}`
              }
            });
          }, 1000);
          
          resolve();
        };
    
        this.socket.onclose = (event) => {
          console.log(`WebSocket disconnected with code: ${event.code}, reason: ${event.reason || 'No reason provided'}`);
          
          // Log more details about common close codes
          if (event.code === 1000) {
            console.log('Normal closure: The connection successfully completed the purpose for which it was created.');
          } else if (event.code === 1001) {
            console.log('Going Away: The endpoint is going away (e.g., server shutdown).');
          } else if (event.code === 1006) {
            console.log('Abnormal Closure: No close frame received. Possible network issues or server crash.');
          } else if (event.code === 1008) {
            console.log('Policy Violation: Server rejected connection due to policy violation (often authentication/authorization).');
            // Emit authentication error for code 1008 which is often used for auth failures
            this.emit('authentication_error', { 
              message: 'Connection rejected: ' + (event.reason || 'IP address not allowed/Authentication failure'),
              code: 'policy_violation'
            });
            // Stop reconnection attempts on authentication policy violations
            this.intentionalClose = true;
            this.retryCount = this.maxRetryCount; // Ensure we don't retry
            this.connectionCount = 0; // Reset connection count to prevent auto-reconnect
            
            // Clear any existing retry timeouts
            if (this.retryTimeout) {
              clearTimeout(this.retryTimeout);
              this.retryTimeout = null;
            }
            
            // Do not continue with normal close handling
            return;
          } else if (event.code === 1011) {
            console.log('Internal Server Error: The server encountered an unexpected condition.');
          } else if (event.code === 1015) {
            console.log('TLS Handshake Failure: The connection was closed due to a failure to perform a TLS handshake.');
          }
          
          this.authenticated = false;
          this.connectionPromise = null;
          this.emit('disconnect', { code: event.code, reason: event.reason });
          
          // Only retry if it wasn't an intentional close and we have active references
          if (!this.intentionalClose && this.connectionCount > 0 && this.retryCount < this.maxRetryCount) {
            const delay = this.calculateRetryDelay();
            console.log(`Retrying connection in ${delay / 1000} seconds...`);
            
            this.retryTimeout = setTimeout(() => {
              this.retryCount++;
              this.connect(this.connectionUrl);
            }, delay);
          } else if (this.retryCount >= this.maxRetryCount) {
            console.error('Maximum retry count reached. Giving up on reconnection.');
            // Reset connection count to prevent further reconnection attempts
            this.connectionCount = 0;
            // Set intentional close to prevent automatic reconnection
            this.intentionalClose = true;
            // Emit max retries reached event
            this.emit('max_retries_reached', {});
          }
        };
    
        this.socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          // More detailed debugging for network errors
          console.debug('Browser WebSocket readyState:', this.socket.readyState);
          console.debug('Current URL:', connectionUrl);
          
          this.emit('error', error);
          if (this.socket.readyState === WebSocket.CONNECTING) {
            this.connectionPromise = null;
            reject(error);
          }
        };
        
        this.socket.onmessage = (event) => {
          try {
            // Log raw message data
            // console.log('[WebSocket ssRaw Message]', event.data);
            
            const data = JSON.parse(event.data);
           
            if(data.type !== 'feed') {
              // console.log('[WebSocket Raw Message]', data);
            }
            
            // Specifically log price updates
            if (data.type === 'price-update' || data.type === 'instrument-price-update' || data.type === 'feed') {
              // Handle the specific format for 'feed' type messages
              if (data.type === 'feed' && data.data && data.data.MarketDataEntries) {
                const entries = data.data.MarketDataEntries;
                const bid = entries.find(entry => entry.Type === 'Bid');
                const ask = entries.find(entry => entry.Type === 'Ask');
                
                // console.log('[WebSocket Price Update]', {
                //   type: data.type,
                //   symbol: data.data.symbol || 'No symbol',
                //   bid: bid ? bid.Price : 'No bid',
                //   ask: ask ? ask.Price : 'No ask',
                //   bidQuantity: bid ? bid.Quantity : 'No quantity',
                //   askQuantity: ask ? ask.Quantity : 'No quantity'
                // });
                
                // Cache the price data
                if (data.data.symbol) {
                  const symbol = data.data.symbol;
                  const priceData = {
                    symbol,
                    bid: bid ? bid.Price : null,
                    ask: ask ? ask.Price : null,
                    bidQuantity: bid ? bid.Quantity : null,
                    askQuantity: ask ? ask.Quantity : null
                  };
                  
                  // Store in cache
                  this.cache.prices.set(symbol, priceData);
                  this.cache.lastUpdated.set(`price_${symbol}`, Date.now());
                  
                  // console.log('[Emitting price-feed event]', priceData);
                  this.emit('price-feed', priceData);
                }
              } else {
                // Original logging for other price update types
                // console.log('[WebSocket Price Update]', {
                //   type: data.type,
                //   symbol: data.symbol || (data.data && data.data.symbol) || 'No symbol',
                //   price: data.price || (data.data && data.data.price) || 'No price',
                //   change: data.change || data['24hour-change'] || 
                //         (data.data && (data.data.change || data.data['24hour-change'])) || 'No change'
                // });
              }
            }
            
            // Cache instrument data
            if (data.type === 'instruments' && Array.isArray(data.data)) {
              data.data.forEach(instrument => {
                if (instrument.symbol) {
                  this.cache.instruments.set(instrument.symbol, instrument);
                  this.cache.lastUpdated.set(`instrument_${instrument.symbol}`, Date.now());
                }
              });
            }
            
            this.emit('message', data);
            
            // Handle connection_established message by authenticating
            if (data.type === 'connection_established' && !this.authenticated) {
              this.authenticate();
            }
            
            // Handle authentication success - fix to match exact server response type
            if (data.type === 'authentication_success' || data.type === 'auth_success' || data.type === 'authentication_successful') {
              console.log('WebSocket authentication successful');
              this.authenticated = true;
              this.emit('authenticated', data);
              
              // Show authentication success notification
              this.emit('new-notification', {
                type: 'new-notification',
                data: {
                  message: 'Authentication successful',
                  type: 'success',
                  status: 'Authenticated',
                  notificationId: `ws-auth-${Date.now()}`
                }
              });
              
              // Process any queued messages after authentication succeeds
              this.processQueue();
            }
            
            // Handle authentication failure
            if (data.type === 'authentication_error' || data.type === 'auth_error') {
              console.error('WebSocket authentication failed:', data.message);
              this.emit('authentication_error', data);
              
              // Show authentication error notification
              this.emit('new-notification', {
                type: 'new-notification',
                data: {
                  message: data.message || 'Authentication failed',
                  type: 'error',
                  status: 'Auth Error',
                  notificationId: `ws-auth-error-${Date.now()}`
                }
              });
              
              // Stop reconnection attempts on authentication error from server
              if (data.code === 'invalid_token' || data.code === 'expired_token' || data.code === 'token_validation_error') {
                console.log('Authentication failed due to token issue. Stopping reconnection attempts');
                this.forceDisconnect(true);
              }
            }
            
            // If the message has a type, emit it as a specific event
            if (data.type) {
              this.emit(data.type, data.payload || data);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
            console.debug('Raw message data:', event.data);
          }
        };
        
      } catch (error) {
        console.error('Exception while creating WebSocket:', error);
        this.emit('error', error);
        this.connectionPromise = null;
        reject(error);
        
        // Retry on exception if not at max retries
        if (this.connectionCount > 0 && this.retryCount < this.maxRetryCount) {
          const delay = this.calculateRetryDelay();
          console.log(`Error creating WebSocket. Retrying in ${delay / 1000} seconds...`);
          
          this.retryTimeout = setTimeout(() => {
            this.retryCount++;
            this.connect(connectionUrl);
          }, delay);
        } else {
          console.error('Maximum retry count reached after exception. Giving up on reconnection.');
          this.emit('max_retries_reached', {});
        }
      }
    });

    return this.connectionPromise;
  }

  calculateRetryDelay() {
    // Exponential backoff with jitter: 2^n * 1000 + random(0-1000)ms
    return Math.min(30000, Math.pow(2, this.retryCount) * 1000 + Math.random() * 1000);
  }

  authenticate() {
    try {
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token found in storage');
        this.emit('authentication_error', { 
          message: 'No authentication token available. Please log in again.',
          code: 'no_token'
        });
        // Stop reconnection attempts when token is missing
        this.forceDisconnect(true);
        return;
      }
      
      // Basic token validation
      if (typeof token !== 'string' || token.trim() === '') {
        console.error('Invalid token format');
        this.emit('authentication_error', { 
          message: 'Invalid authentication token. Please log in again.',
          code: 'invalid_token_format'
        });
        // Stop reconnection attempts when token is invalid
        this.forceDisconnect(true);
        return;
      }
      
      // Send authentication message
      const authMessage = {
        type: 'authentication',
        token: token
      };
      
      console.log('Sending authentication request to WebSocket');
      this.send(authMessage);
      
      // Set a timeout to detect authentication timeout
      setTimeout(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN && !this.authenticated) {
          console.warn('Authentication timed out after 10 seconds');
          this.emit('authentication_error', { 
            message: 'Authentication timed out. Server did not respond.',
            code: 'auth_timeout'
          });
          // Stop reconnection attempts on timeout
          this.forceDisconnect(true);
        }
      }, 10000);
      
    } catch (error) {
      console.error('Error during WebSocket authentication:', error);
      this.emit('authentication_error', { 
        message: `Authentication error: ${error.message || 'Unknown error'}`,
        code: 'auth_error'
      });
      // Stop reconnection attempts on error
      this.forceDisconnect(true);
    }
  }

  /**
   * Release WebSocket connection reference
   * Connection is only closed when all references are released
   */
  disconnect() {
    // Decrement the connection count, but don't go below 0
    this.connectionCount = Math.max(0, this.connectionCount - 1);
    console.log(`WebSocket disconnect called. Remaining references: ${this.connectionCount}`);
    
    // Only close the actual connection if no more references
    if (this.connectionCount === 0) {
      this.intentionalClose = true;
      
      // Clear any pending retry attempts
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
      }
      
      if (this.socket) {
        console.log('Closing WebSocket connection as all references have been released');
        this.socket.close();
        this.socket = null;
        this.authenticated = false;
        this.connectionPromise = null;
      }
    }
  }

  /**
   * Force disconnect from WebSocket server and clear all references
   * This is used when handling authentication failures
   */
  forceDisconnect(isAuthError = false) {
    // Log when method is called for debugging
    console.log('Force disconnect called on WebSocket service');
    
    // If already intentionally closing, don't repeat the process
    if (this.intentionalClose) {
      console.log('WebSocket already disconnecting, preventing duplicate cleanup');
      return;
    }
    
    // Set intentional close to prevent auto-reconnection
    this.intentionalClose = true;
    
    // Reset connection count to ensure we don't try to maintain connection
    this.connectionCount = 0;
    
    // Clear any existing retry timeouts
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    
    // Reset connection promise
    this.connectionPromise = null;
    
    // Clear authentication flag
    this.authenticated = false;
    
    // Close socket if it exists and is connected
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      try {
        console.log('Closing active WebSocket connection');
        this.socket.close(1000, 'User logout or authentication failure');
      } catch (error) {
        console.error('Error while force closing WebSocket:', error);
      }
    } else if (this.socket) {
      console.log(`WebSocket already in state: ${this.getReadyStateText(this.socket.readyState)}`);
    } else {
      console.log('No active WebSocket connection to close');
    }
    
    // If this is an auth error, emit an authentication_error event after force disconnect
    if (isAuthError) {
      console.log('Emitting force disconnect authentication error');
      this.emit('authentication_error', {
        message: 'Connection terminated due to authentication issues. Please log in again.',
        code: 'force_disconnect_auth'
      });
    }
    
    // Reset reconnection parameters
    this.retryCount = 0;
    this.retryDelay = 1000;
    
    // Set a timeout to reset the intentional close flag after some time
    // This ensures we can reconnect later if needed
    setTimeout(() => {
      this.intentionalClose = false;
    }, 2000);
  }
  
  /**
   * Get text description of WebSocket ready state
   * @param {number} readyState - The WebSocket readyState value
   * @returns {string} Human-readable description of the ready state
   */
  getReadyStateText(readyState) {
    switch(readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN: return 'OPEN';
      case WebSocket.CLOSING: return 'CLOSING';
      case WebSocket.CLOSED: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  }

  /**
   * Send data to WebSocket server
   * @param {Object} data - Data to send
   */
  send(data) {
    // Always log what we're sending for debugging
    console.log('[WebSocket Sending]', data);
    
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket is not open, queueing message');
      
      // Ensure we have a queue
      if (!this.messageQueue) {
        this.messageQueue = [];
      }
      
      // Add to queue
      this.messageQueue.push(data);
      
      // Return to prevent sending when not connected
      return;
    }
    
    try {
      // Convert to JSON if not already a string
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      
      // Send the message
      this.socket.send(message);
      
      // Log confirmation of message sent
      // console.log('[WebSocket Sent]', {
      //   type: data.type || 'Unknown type',
      //   sentAt: new Date().toISOString(),
      //   messageSize: message.length
      // });
    } catch (error) {
      console.error('Error sending message to WebSocket:', error);
      
      // Queue the message for retry
      if (!this.messageQueue) {
        this.messageQueue = [];
      }
      this.messageQueue.push(data);
    }
  }

  // Add method to process queued messages
  processQueue() {
    if (!this.messageQueue || this.messageQueue.length === 0) {
      return;
    }
    
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log(`Processing ${this.messageQueue.length} queued messages`);
      
      // Process all queued messages
      this.messageQueue.forEach(message => {
        try {
          this.socket.send(JSON.stringify(message));
          console.log('Sent queued message:', message.type || 'unknown type');
        } catch (error) {
          console.error('Error sending queued message:', error);
        }
      });
      
      // Clear the queue
      this.messageQueue = [];
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => callback(data));
    }
  }
  
  isAuthenticated() {
    return this.authenticated;
  }
  
  /**
   * Get the current connection state
   * @returns {string} Connection state: 'connected', 'connecting', 'disconnected'
   */
  getConnectionState() {
    if (!this.socket) {
      return 'disconnected';
    }
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
      default:
        return 'disconnected';
    }
  }

  /**
   * Reconnect to the WebSocket server, potentially with a fresh endpoint URL
   * Use this when you want to force a reconnection with potentially new endpoints
   */
  reconnect() {
    console.log('Manual reconnection requested');
    
    // Force disconnect first
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    
    // Reset connection state
    this.retryCount = 0;
    this.authenticated = false;
    this.connectionPromise = null;
    
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    
    // Get a fresh URL from storage (in case endpoints were updated)
    const freshUrl = this.getWebSocketUrl();
    
    // Connect with the fresh URL
    return this.connect(freshUrl);
  }

  /**
   * Get cached price data for a symbol if available and not expired
   * @param {string} symbol - The market symbol to get price data for
   * @returns {Object|null} The cached price data or null if not cached or expired
   */
  getCachedPrice(symbol) {
    if (!symbol) return null;
    
    const lastUpdated = this.cache.lastUpdated.get(`price_${symbol}`);
    const now = Date.now();
    
    // Check if we have cached data and it's not expired
    if (lastUpdated && (now - lastUpdated) < this.cache.ttl.prices) {
      return this.cache.prices.get(symbol);
    }
    
    return null;
  }
  
  /**
   * Get cached instrument data if available and not expired
   * @param {string} symbol - The instrument symbol
   * @returns {Object|null} The cached instrument data or null if not cached or expired
   */
  getCachedInstrument(symbol) {
    if (!symbol) return null;
    
    const lastUpdated = this.cache.lastUpdated.get(`instrument_${symbol}`);
    const now = Date.now();
    
    // Check if we have cached data and it's not expired
    if (lastUpdated && (now - lastUpdated) < this.cache.ttl.instruments) {
      return this.cache.instruments.get(symbol);
    }
    
    return null;
  }
  
  /**
   * Clear all cached data or specific cache types
   * @param {string} [type] - Optional cache type to clear ('prices' or 'instruments')
   */
  clearCache(type) {
    if (!type) {
      // Clear all cache
      this.cache.prices.clear();
      this.cache.instruments.clear();
      this.cache.lastUpdated.clear();
      console.log('Cleared all WebSocket cache');
    } else if (type === 'prices') {
      this.cache.prices.clear();
      // Clear all price-related lastUpdated entries
      for (const key of this.cache.lastUpdated.keys()) {
        if (key.startsWith('price_')) {
          this.cache.lastUpdated.delete(key);
        }
      }
      console.log('Cleared price cache');
    } else if (type === 'instruments') {
      this.cache.instruments.clear();
      // Clear all instrument-related lastUpdated entries
      for (const key of this.cache.lastUpdated.keys()) {
        if (key.startsWith('instrument_')) {
          this.cache.lastUpdated.delete(key);
        }
      }
      console.log('Cleared instruments cache');
    }
  }
}

// Create a singleton instance
const websocketService = new WebSocketService();
export default websocketService; 